import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Sparkles, LogOut, Calendar, Eye, MousePointer, Star,
  Plus, RefreshCw, BarChart3, CreditCard, ChevronRight,
  TrendingUp, ArrowUpRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "../theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HostDashboard() {
  const { isDark } = useTheme();
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventAnalytics, setEventAnalytics] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("neuromap_host_token");
  const hostName = localStorage.getItem("neuromap_host_name");

  const [eventForm, setEventForm] = useState({
    title: "", description: "", town: "", country: "",
    event_type: "meetup", contact_info: "", recurring: false, schedule: ""
  });
  const [appSettings, setAppSettings] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate("/host");
      return;
    }
    fetchDashboard();
    axios.get(`${API}/settings/public`).then(r => setAppSettings(r.data)).catch(() => {});
  }, [token, navigate]);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchDashboard = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/hosts/dashboard`, { headers: authHeaders });
      setDashboard(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("neuromap_host_token");
        navigate("/host");
      } else {
        toast.error("Failed to load dashboard");
      }
    }
    setIsLoading(false);
  };

  const fetchEventAnalytics = async (eventId) => {
    try {
      const res = await axios.get(`${API}/hosts/events/${eventId}/analytics`, { headers: authHeaders });
      setEventAnalytics(res.data);
      setSelectedEvent(eventId);
    } catch (err) {
      toast.error("Failed to load analytics");
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim() || !eventForm.town.trim() || !eventForm.contact_info.trim()) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/hosts/events`, eventForm, { headers: authHeaders });
      toast.success("Event submitted for approval!");
      setShowNewEvent(false);
      setEventForm({
        title: "", description: "", town: "", country: "",
        event_type: "meetup", contact_info: "", recurring: false, schedule: ""
      });
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to create event");
    }
  };

  const handleMakeFeatured = async (eventId) => {
    try {
      const origin = window.location.origin;
      const res = await axios.post(
        `${API}/checkout/create`,
        { event_id: eventId, origin_url: origin },
        { headers: authHeaders }
      );
      // Redirect to Stripe checkout
      window.location.href = res.data.url;
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create checkout";
      toast.error(msg);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("neuromap_host_token");
    localStorage.removeItem("neuromap_host_id");
    localStorage.removeItem("neuromap_host_name");
    localStorage.removeItem("neuromap_host_email");
    navigate("/host");
  };

  if (isLoading && !dashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: isDark ? "#0f172a" : "#f8fafc" }} data-testid="host-loading">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  const { totals, events, transactions, daily_analytics } = dashboard || {};

  return (
    <div className="min-h-screen h-screen overflow-y-auto" style={{ background: isDark ? "#0f172a" : "#f8fafc" }} data-testid="host-dashboard">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between" style={{ background: isDark ? "#1e293b" : "white" }} data-testid="host-header">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-800" style={{ fontFamily: "Nunito" }}>{appSettings?.app_name || "Labelled"}</h1>
              <p className="text-xs text-slate-500">Event Host Dashboard</p>
            </div>
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-600 font-medium hidden sm:inline">Hi, {hostName}</span>
          <button onClick={fetchDashboard} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="refresh-dashboard-btn">
            <RefreshCw className={`w-5 h-5 text-slate-600 ${isLoading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-600 hover:text-slate-800 font-medium transition-colors" data-testid="host-logout-btn">
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="host-stats-grid">
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Events</span>
            </div>
            <p className="text-3xl font-black text-slate-800" style={{ fontFamily: "Nunito" }} data-testid="total-events-stat">
              {totals?.total_events || 0}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Featured</span>
            </div>
            <p className="text-3xl font-black text-slate-800" style={{ fontFamily: "Nunito" }} data-testid="featured-events-stat">
              {totals?.featured_events || 0}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Views</span>
            </div>
            <p className="text-3xl font-black text-slate-800" style={{ fontFamily: "Nunito" }} data-testid="total-views-stat">
              {totals?.total_views || 0}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <MousePointer className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Clicks</span>
            </div>
            <p className="text-3xl font-black text-slate-800" style={{ fontFamily: "Nunito" }} data-testid="total-clicks-stat">
              {totals?.total_clicks || 0}
            </p>
          </div>
        </div>

        {/* Events Section */}
        <section data-testid="host-events-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
              <Calendar className="w-5 h-5 text-indigo-600" />
              Your Events
            </h2>
            <button
              onClick={() => setShowNewEvent(!showNewEvent)}
              className="btn-primary text-sm py-2 px-4"
              data-testid="create-event-btn"
            >
              <Plus className="w-4 h-4" />
              New Event
            </button>
          </div>

          {/* New Event Form */}
          {showNewEvent && (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mb-6 animate-fade-in" data-testid="new-event-form">
              <h3 className="font-bold text-lg text-slate-800 mb-4" style={{ fontFamily: "Nunito" }}>Create New Event</h3>
              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Title *</label>
                    <input type="text" value={eventForm.title} onChange={(e) => setEventForm({...eventForm, title: e.target.value})} placeholder="ADHD Support Group" className="input-field" data-testid="new-event-title" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Type</label>
                    <select value={eventForm.event_type} onChange={(e) => setEventForm({...eventForm, event_type: e.target.value})} className="input-field" data-testid="new-event-type">
                      <option value="meetup">Meetup</option>
                      <option value="support_group">Support Group</option>
                      <option value="workshop">Workshop</option>
                      <option value="social">Social Event</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                  <textarea value={eventForm.description} onChange={(e) => setEventForm({...eventForm, description: e.target.value})} placeholder="What's your event about?" className="textarea-field" data-testid="new-event-desc" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Town/City *</label>
                    <input type="text" value={eventForm.town} onChange={(e) => setEventForm({...eventForm, town: e.target.value})} placeholder="London" className="input-field" data-testid="new-event-town" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Country *</label>
                    <input type="text" value={eventForm.country} onChange={(e) => setEventForm({...eventForm, country: e.target.value})} placeholder="United Kingdom" className="input-field" data-testid="new-event-country" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Contact Info *</label>
                  <input type="text" value={eventForm.contact_info} onChange={(e) => setEventForm({...eventForm, contact_info: e.target.value})} placeholder="Email or website" className="input-field" data-testid="new-event-contact" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={eventForm.recurring} onChange={(e) => setEventForm({...eventForm, recurring: e.target.checked})} className="w-4 h-4 rounded" data-testid="new-event-recurring" />
                  <span className="text-sm font-medium text-slate-700">Recurring event</span>
                </div>
                {eventForm.recurring && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Schedule</label>
                    <input type="text" value={eventForm.schedule} onChange={(e) => setEventForm({...eventForm, schedule: e.target.value})} placeholder="Every Tuesday 7pm" className="input-field" data-testid="new-event-schedule" />
                  </div>
                )}
                <div className="flex gap-3">
                  <button type="submit" className="btn-primary" data-testid="submit-new-event-btn">Create Event</button>
                  <button type="button" onClick={() => setShowNewEvent(false)} className="btn-secondary" data-testid="cancel-new-event-btn">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {/* Events List */}
          {(!events || events.length === 0) ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">You haven't created any events yet</p>
              <button onClick={() => setShowNewEvent(true)} className="btn-primary text-sm" data-testid="create-first-event-btn">
                <Plus className="w-4 h-4" />
                Create Your First Event
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {events.map((event) => (
                <div key={event.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`host-event-${event.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>{event.title}</h3>
                        {event.featured && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full flex items-center gap-1" data-testid={`featured-badge-${event.id}`}>
                            <Star className="w-3 h-3" /> Featured
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-indigo-600 font-medium">{event.town}, {event.country}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${event.approved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {event.approved ? "Live" : "Pending"}
                    </span>
                  </div>

                  {event.description && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{event.description}</p>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    {event.featured ? (
                      <button
                        onClick={() => fetchEventAnalytics(event.id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium transition-colors"
                        data-testid={`view-analytics-${event.id}`}
                      >
                        <BarChart3 className="w-4 h-4" />
                        Analytics
                      </button>
                    ) : event.approved ? (
                      <button
                        onClick={() => handleMakeFeatured(event.id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg text-sm font-bold transition-all"
                        data-testid={`make-featured-${event.id}`}
                      >
                        <Star className="w-4 h-4" />
                        Make Featured - £5/mo
                      </button>
                    ) : null}
                    <span className="text-xs text-slate-400">{event.event_type}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Event Analytics Detail */}
        {selectedEvent && eventAnalytics && (
          <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm animate-fade-in" data-testid="event-analytics-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Analytics: {eventAnalytics.event?.title}
                </h2>
                <p className="text-sm text-slate-500 mt-1">Last 30 days performance</p>
              </div>
              <button
                onClick={() => { setSelectedEvent(null); setEventAnalytics(null); }}
                className="text-sm text-slate-500 hover:text-slate-700 font-medium"
                data-testid="close-analytics-btn"
              >
                Close
              </button>
            </div>

            {/* Analytics Summary */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-emerald-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-700">Total Views</span>
                </div>
                <p className="text-3xl font-black text-emerald-800" style={{ fontFamily: "Nunito" }} data-testid="analytics-views">
                  {eventAnalytics.totals?.views || 0}
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <MousePointer className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-700">Total Clicks</span>
                </div>
                <p className="text-3xl font-black text-purple-800" style={{ fontFamily: "Nunito" }} data-testid="analytics-clicks">
                  {eventAnalytics.totals?.clicks || 0}
                </p>
              </div>
            </div>

            {/* Daily Breakdown */}
            {eventAnalytics.analytics?.length > 0 ? (
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Daily Breakdown</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {eventAnalytics.analytics.map((day) => (
                    <div key={day.date} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg" data-testid={`analytics-day-${day.date}`}>
                      <span className="text-sm font-medium text-slate-700">{day.date}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-emerald-600 flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {day.views || 0}
                        </span>
                        <span className="text-sm text-purple-600 flex items-center gap-1">
                          <MousePointer className="w-3 h-3" /> {day.clicks || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <TrendingUp className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No analytics data yet. Views and clicks will appear here.</p>
              </div>
            )}
          </section>
        )}

        {/* Payment History */}
        {transactions && transactions.length > 0 && (
          <section data-testid="payment-history-section">
            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Payment History
            </h2>
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="divide-y divide-slate-100">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-4" data-testid={`transaction-${tx.id}`}>
                    <div>
                      <p className="font-medium text-slate-800">Featured Event</p>
                      <p className="text-sm text-slate-500">{new Date(tx.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-slate-800">£{tx.amount?.toFixed(2)}</span>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        tx.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                        tx.payment_status === "initiated" ? "bg-amber-100 text-amber-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        {tx.payment_status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
